/**
 * AuthContext — fonte da verdade pro estado de autenticação.
 *
 *   useAuth() -> { user, login, logout, refresh, ... helpers de RBAC }
 *
 * Estratégia:
 *   1. Token vive em localStorage (axios interceptor injeta como Bearer).
 *   2. No mount inicial, se tem token, chama /auth/me; se 401, limpa tudo.
 *   3. Escuta evento global `auth:expired` (disparado pelo interceptor).
 *   4. Renova o token sozinho enquanto a aba estiver aberta.
 *
 * SOBRE A RENOVAÇÃO (item 4), que existe por causa do modo painel:
 *
 * Uma tela pendurada na parede fica aberta por semanas, e com token de 24 h
 * ela voltava pra página de login uma vez por dia. Ninguém sobe na escada
 * todo dia. Então a aba aberta troca o token por um novo na metade da vida
 * dele, e a sessão dura enquanto a tela estiver viva.
 *
 * Isto NÃO é token eterno, e a diferença importa: a troca exige um token
 * ainda válido. Aba fechada, máquina reiniciada ou um dia sem rede encerram
 * a sessão exatamente como antes, e um token vazado continua valendo só o
 * tempo que já valia. O servidor relê o usuário a cada renovação, então
 * desativar alguém derruba a aba dele na renovação seguinte.
 *
 * O limite conhecido: temporizador em aba de fundo é estrangulado pelo
 * navegador, e máquina suspensa não dispara nenhum. Por isso a renovação
 * também acontece quando a aba volta a ficar visível e quando a rede volta —
 * e por isso ela é agendada na METADE da vida do token, não nos últimos
 * minutos: é a folga que cobre um temporizador atrasado.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { authApi } from "@/api/auth";
import { getStoredToken, setStoredToken } from "@/api/client";
import type { Role, User } from "@/types";
import { identificar } from "@/obs";

/** Quando renovar, como fração da vida do token. Metade dá uma janela larga
 *  pra temporizador atrasado e pra rede que caiu por um tempo. */
const FRACAO_DE_RENOVACAO = 0.5;
/** Piso do agendamento — sem ele, token de vida curta viraria laço apertado. */
const MINIMO_MS = 60_000;
/** Nova tentativa depois de uma renovação que falhou por rede. */
const REPETIR_APOS_FALHA_MS = 60_000;

/**
 * Lê `iat` e `exp` do JWT sem validar assinatura.
 *
 * Validar aqui não faria sentido: o cliente não tem a chave, e quem decide
 * se o token vale é o servidor. Isto serve só pra saber QUANDO renovar.
 *
 * O `iat` importa tanto quanto o `exp`: com os dois dá pra saber a vida
 * TOTAL do token, e a regra de renovação vira "renove na segunda metade"
 * — que continua certa se um dia o `jwt_expires_minutes` mudar. Sem o
 * `iat`, a regra teria que chutar um número fixo e envelhecer errado.
 */
function vidaDoToken(token: string): { vence: number; total: number } | null {
  try {
    const parte = token.split(".")[1];
    if (!parte) return null;
    const json = atob(parte.replace(/-/g, "+").replace(/_/g, "/"));
    const carga = JSON.parse(json) as { exp?: unknown; iat?: unknown };
    if (typeof carga.exp !== "number") return null;
    const vence = carga.exp * 1000;
    const total = typeof carga.iat === "number"
      ? vence - carga.iat * 1000
      : vence - Date.now();
    return { vence, total: Math.max(total, MINIMO_MS) };
  } catch {
    return null;
  }
}

// Hierarquia de roles (super_admin é o mais alto).
const ROLE_LEVEL: Record<Role, number> = {
  viewer: 1,
  operator: 2,
  admin: 3,
  super_admin: 4,
};

interface AuthState {
  user: User | null;
  loading: boolean;
  initialized: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  hasRole: (...roles: Role[]) => boolean;
  hasMinRole: (role: Role) => boolean;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // O cache do react-query não sabe de quem é: as chaves não levam usuário
  // nem tenant (`["equipments", {}]`, `["active-alerts", null]`). Sem limpar
  // na troca de sessão, quem entra depois num tablet compartilhado via os
  // ativos e a planta de quem saiu — e, se a busca dele desse 403, o
  // react-query mantinha o dado anterior na tela. Toda saída de sessão e
  // toda entrada passam por `esquecerSessao`.
  const qc = useQueryClient();
  const esquecerSessao = useCallback(() => {
    qc.cancelQueries();
    qc.clear();
  }, [qc]);

  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    initialized: false,
  });

  // Só o ID vai pro Sentry — nunca e-mail. "Qual usuário viu esse erro" é
  // pergunta de suporte; o resto é dado pessoal que não precisa sair daqui.
  useEffect(() => {
    identificar(state.user?.id ?? null);
  }, [state.user?.id]);

  const refresh = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setState({ user: null, loading: false, initialized: true });
      return;
    }
    try {
      const user = await authApi.me();
      setState({ user, loading: false, initialized: true });
    } catch {
      setStoredToken(null);
      setState({ user: null, loading: false, initialized: true });
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { access_token } = await authApi.login(email, password);
      setStoredToken(access_token);
      const user = await authApi.me();
      esquecerSessao();
      setState({ user, loading: false, initialized: true });
    } catch (err) {
      setState((s) => ({ ...s, loading: false }));
      throw err;
    }
  }, [esquecerSessao]);

  const logout = useCallback(() => {
    setStoredToken(null);
    esquecerSessao();
    setState({ user: null, loading: false, initialized: true });
  }, [esquecerSessao]);

  // Mount inicial — tenta restaurar sessão.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // --- Renovação silenciosa ------------------------------------------------
  // `geracao` reagenda o temporizador depois de cada troca de token: sem
  // isso o efeito rodaria uma vez e a segunda renovação nunca aconteceria.
  const [geracao, setGeracao] = useState(0);
  const renovando = useRef(false);

  const renovarToken = useCallback(async () => {
    if (renovando.current) return;
    const token = getStoredToken();
    if (!token) return;
    renovando.current = true;
    try {
      const { access_token } = await authApi.renovar();
      setStoredToken(access_token);
      setGeracao((g) => g + 1);
    } catch {
      // Silêncio de propósito: 401 já é tratado pelo interceptor, que
      // desloga. Qualquer outra falha é rede, e derrubar a sessão por uma
      // renovação que não saiu seria trocar um problema temporário por um
      // login no meio do expediente.
    } finally {
      renovando.current = false;
    }
  }, []);

  useEffect(() => {
    if (!state.user) return;
    const token = getStoredToken();
    if (!token) return;

    const vida = vidaDoToken(token);
    // Alvo: o instante em que resta metade da vida. Se já passou disso
    // (token restaurado do storage depois de horas), o piso dispara logo.
    const espera = vida
      ? Math.max(MINIMO_MS, vida.vence - vida.total * FRACAO_DE_RENOVACAO - Date.now())
      : REPETIR_APOS_FALHA_MS;

    const t = setTimeout(() => { void renovarToken(); }, espera);
    return () => clearTimeout(t);
  }, [state.user, geracao, renovarToken]);

  // Temporizador de aba em segundo plano é estrangulado, e máquina suspensa
  // não dispara nenhum. Voltar a ficar visível, ou a rede voltar, é o
  // momento em que vale conferir.
  useEffect(() => {
    if (!state.user) return;

    function conferir() {
      const token = getStoredToken();
      if (!token) return;
      const vida = vidaDoToken(token);
      if (!vida) return;
      const restante = vida.vence - Date.now();
      // Já morreu: renovar não adianta — o servidor recusa, e a próxima
      // chamada de API dispara o 401 que desloga.
      if (restante <= 0) return;
      // Na segunda metade da vida, aproveita a janela.
      if (restante < vida.total * FRACAO_DE_RENOVACAO) void renovarToken();
    }
    function aoFicarVisivel() {
      if (document.visibilityState === "visible") conferir();
    }

    document.addEventListener("visibilitychange", aoFicarVisivel);
    window.addEventListener("online", conferir);
    return () => {
      document.removeEventListener("visibilitychange", aoFicarVisivel);
      window.removeEventListener("online", conferir);
    };
  }, [state.user, renovarToken]);

  // Listener global pra 401 do interceptor.
  useEffect(() => {
    const handler = () => {
      setStoredToken(null);
      esquecerSessao();
      setState({ user: null, loading: false, initialized: true });
    };
    window.addEventListener("auth:expired", handler);
    return () => window.removeEventListener("auth:expired", handler);
  }, [esquecerSessao]);

  const value = useMemo<AuthContextValue>(() => {
    const user = state.user;
    return {
      ...state,
      login,
      logout,
      refresh,
      hasRole: (...roles: Role[]) => !!user && roles.includes(user.role),
      hasMinRole: (role: Role) =>
        !!user && ROLE_LEVEL[user.role] >= ROLE_LEVEL[role],
      isSuperAdmin: user?.role === "super_admin",
    };
  }, [state, login, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth precisa estar dentro de <AuthProvider>");
  return ctx;
}
